import * as Sentry from '@sentry/react'
import axios from 'axios'
import axiosRetry from 'axios-retry'

/**
 * A second client, for the Patient API (Bun/Elysia, care check-ins) — separate from `lib/http`,
 * which is bound to NodeAPI.
 *
 * It carries the same Clerk bearer token: the Patient API authenticates with Clerk and gates the
 * trend read on `CARE_VIEW`, a permission every prescriber role already holds, so no extra
 * credential is involved. Its CORS allowlist already covers `*.evaro.com` and localhost.
 *
 * Deliberately quieter than `lib/http`: no error toast. Care data is a panel inside the medication
 * request rather than something the prescriber asked for, so a failed trend read degrades to an
 * empty panel instead of interrupting the decision they are actually making. Failures still reach
 * Sentry.
 */
/**
 * What this client rejects with.
 *
 * Rejecting the raw axios error hands callers the whole request/response graph; rejecting only
 * `response.data` throws the status away, leaving a caller unable to tell a 404 ("this patient has
 * no care history") from a 500. This keeps both — the body to read, the status to branch on.
 */
export class PatientApiError extends Error {
  readonly status?: number
  readonly body?: unknown

  constructor(message: string, status?: number, body?: unknown) {
    super(message)
    this.name = 'PatientApiError'
    this.status = status
    this.body = body
  }
}

const messageFrom = (body: unknown, fallback: string): string => {
  if (typeof body === 'string' && body) return body
  if (typeof body === 'object' && body !== null) {
    const { message, error } = body as { message?: unknown; error?: unknown }
    if (typeof message === 'string' && message) return message
    if (typeof error === 'string' && error) return error
  }
  return fallback
}

const patientHttp = axios.create({
  baseURL: import.meta.env.VITE_PATIENT_API_URL,
  timeout: 15000,
  timeoutErrorMessage: 'Request timed out',
  adapter: 'fetch'
})

patientHttp.interceptors.request.use(async req => {
  const token = await window.Clerk.session?.getToken()
  if (!token) return Promise.reject(new Error('No authentication token available'))

  req.headers.Authorization = `Bearer ${token}`
  return req
})

patientHttp.interceptors.response.use(
  res => res.data,
  error => {
    const status = error.response?.status
    const url: string = error.config?.url

    if (axios.isCancel(error)) return Promise.reject(error)

    // A patient with no care history is an expected shape of "nothing to draw", not an incident.
    if (status !== 404) {
      const isFEError = status >= 400 && status < 500
      const hashedUrl = url
        ?.split('/')
        .map((part: string) => (part.length === 30 ? ':hash' : part))
        .join('/')

      Sentry.captureMessage(`Patient API Error : ${error.config?.method?.toUpperCase()} ${hashedUrl} ${status}`, {
        level: isFEError ? 'debug' : 'error',
        contexts: {
          http_error: {
            hasJWT: !!error.config?.headers?.Authorization,
            response: { body: error.response?.data, status },
            isOnline: window.navigator.onLine,
            code: error.code
          }
        }
      })
    }

    // Status included: a 404 on the trend read means "no care history", which the hook resolves to
    // an empty series rather than an error state, and only a caller that can see the status can
    // tell that apart from a real failure.
    return Promise.reject(
      new PatientApiError(
        messageFrom(error.response?.data, error.message ?? `Patient API request failed (${status ?? 'no status'})`),
        status,
        error.response?.data
      )
    )
  }
)

axiosRetry(patientHttp, {
  shouldResetTimeout: true,
  retryCondition: error => {
    const status = error.response?.status
    return !status || status >= 500
  }
})

export default patientHttp
